import React, { useState, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../utils/api';

/**
 * Step 2: Choose credit pack.
 * Packages (including the free tier) come from the DB via /api/credits/packages.
 */
function PricingStep({ onNext }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch('/api/credits/packages')
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) setPackages(data.data || []);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authenticatedFetch]);

  const freePkg = packages.find(p => !p.isPurchasable);

  if (loading) {
    return (
      <s-stack gap="large">
        <s-stack gap="small" alignItems="center">
          <div className="shimmer-loader" style={{ height: 32, width: '40%', borderRadius: 4 }} />
          <div className="shimmer-loader" style={{ height: 20, width: '30%', borderRadius: 4 }} />
          <s-stack direction="inline" gap="small">
            <div className="shimmer-loader" style={{ height: 24, width: 150, borderRadius: 12 }} />
            <div className="shimmer-loader" style={{ height: 24, width: 120, borderRadius: 12 }} />
          </s-stack>
        </s-stack>
        
        <s-grid gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap="base">
          {[0, 1, 2].map(i => (
            <div key={i} className="shimmer-loader" style={{ height: 340, borderRadius: 8 }} />
          ))}
        </s-grid>
      </s-stack>
    );
  }

  return (
    <s-stack gap="large">

      {/* Header */}
      <s-stack gap="small" alignItems="center">
        <s-heading>Select your starting credit pack</s-heading>
        <s-text tone="subdued">Credits never expire · No monthly fees</s-text>
        <s-stack direction="inline" gap="small" alignItems="center">
          <s-badge tone="info">1 credit = 1 image optimized</s-badge>
          <s-badge>Credits never expire</s-badge>
        </s-stack>
      </s-stack>

      {/* Cards grid */}
      <s-grid gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap="base">
        {packages.map((pkg) => {
          const isFree    = !pkg.isPurchasable;
          const isPopular = pkg.isPopular;
          const features  = pkg.features || [];

          return (
            <s-box
              key={pkg.packageId}
              padding="base"
              border="base"
              borderRadius="base"
              background={isPopular ? 'subdued' : 'base'}
              style={isPopular ? { borderColor: 'var(--p-color-border-emphasis,#005bd3)', borderWidth: '2px' } : {}}
            >
              <s-stack gap="base">

                {/* Plan name + badge row */}
                <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                  <s-text tone="subdued" size="small" type="strong" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {pkg.name}
                  </s-text>
                  {isPopular && <s-badge tone="info">Most Popular</s-badge>}
                  {isFree    && <s-badge>Free</s-badge>}
                </s-stack>

                {/* Price */}
                <s-stack gap="none">
                  <s-heading size="large">
                    {pkg.price === 0 ? 'Free' : `$${Number(pkg.price).toFixed(2)}`}
                  </s-heading>
                  <s-text size="small" tone="subdued">
                    {isFree ? 'Included with first install' : 'One-time · No expiry'}
                  </s-text>
                </s-stack>

                {/* Credits box */}
                <s-box padding="small" background="subdued" borderRadius="base">
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-heading size="small">{pkg.credits.toLocaleString()}</s-heading>
                    <s-text tone="subdued">image credits</s-text>
                  </s-stack>
                  {!isFree && freePkg && (
                    <s-text size="small" tone="subdued">
                      + {freePkg.credits.toLocaleString()} free credits already included
                    </s-text>
                  )}
                </s-box>

                <s-divider />

                {/* Features */}
                {features.length > 0 && (
                  <s-stack gap="small">
                    {features.map((f, i) => (
                      <s-stack key={i} direction="inline" gap="small" alignItems="start">
                        <s-icon source="check-circle" tone="success" />
                        <s-text size="small">{f}</s-text>
                      </s-stack>
                    ))}
                  </s-stack>
                )}

                {/* CTA */}
                {isFree ? (
                  <s-button
                    variant="secondary"
                    onClick={() => onNext(null)}
                  >
                    Start with Free
                  </s-button>
                ) : (
                  <s-button
                    variant={isPopular ? 'primary' : 'secondary'}
                    onClick={() => onNext(pkg)}
                  >
                    {`Select ${pkg.name} — $${Number(pkg.price).toFixed(2)}`}
                  </s-button>
                )}

              </s-stack>
            </s-box>
          );
        })}
      </s-grid>

    </s-stack>
  );
}

export default PricingStep;
